import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { listUsers, ApiError } from '../lib/api';
import { useToast } from '../hooks/useToast';
import { PS, T } from '../lib/theme';
import { fullName } from '../lib/format';
import type { AdminLookupUser } from '../lib/types';

interface Props {
  selected: AdminLookupUser | null;
  onSelect: (user: AdminLookupUser | null) => void;
  /** Default: "Search by name, phone, or email…" */
  placeholder?: string;
  /** Predicate to exclude rows from results (e.g. only coaches). Default: include all. */
  filter?: (u: AdminLookupUser) => boolean;
  /** Optional helper text rendered under the selected chip. */
  hint?: string;
  ariaLabel?: string;
}

const DEBOUNCE_MS = 1000;
const MAX_RESULTS = 10;

export function UserPicker({ selected, onSelect, placeholder, filter, hint, ariaLabel }: Props) {
  const { push } = useToast();
  const [input, setInput] = useState('');
  const [results, setResults] = useState<AdminLookupUser[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Sync the displayed text with the selected user (e.g. when the parent
  // resets the selection externally).
  useEffect(() => {
    if (selected) {
      setInput(displayName(selected));
      setResults([]);
      setOpen(false);
    } else {
      setInput('');
    }
  }, [selected]);

  // Clean up timer + abort controller on unmount.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      abortRef.current?.abort();
    };
  }, []);

  const handleInput = (val: string) => {
    setInput(val);
    if (selected) onSelect(null);

    if (timerRef.current) clearTimeout(timerRef.current);
    abortRef.current?.abort();

    if (!val.trim()) {
      setResults([]);
      setOpen(false);
      return;
    }

    timerRef.current = setTimeout(async () => {
      const ac = new AbortController();
      abortRef.current = ac;
      setSearching(true);
      try {
        const page = await listUsers({ q: val.trim(), limit: MAX_RESULTS }, ac.signal);
        if (ac.signal.aborted) return;
        const filtered = filter ? page.items.filter(filter) : page.items;
        setResults(filtered);
        setOpen(true);
      } catch (err) {
        if (ac.signal.aborted) return;
        push('error', err instanceof ApiError ? err.message : 'Search failed');
      } finally {
        if (!ac.signal.aborted) setSearching(false);
      }
    }, DEBOUNCE_MS);
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        value={input}
        onChange={(e) => handleInput(e.target.value)}
        placeholder={placeholder ?? 'Search by name, phone, or email…'}
        aria-label={ariaLabel}
        style={inputStyle}
        onFocus={() => results.length > 0 && setOpen(true)}
      />
      {searching && (
        <span style={{ ...T.caption, color: PS.textSec, marginLeft: 8 }}>Searching…</span>
      )}
      {selected && (
        <div
          style={{
            marginTop: 6,
            ...T.caption,
            color: PS.success,
            background: PS.successSoft,
            border: `1px solid ${PS.success}`,
            borderRadius: 3,
            padding: 6,
          }}
        >
          Selected: <strong>{fullName(selected)}</strong>
          {selected.phone ? ` · ${selected.phone}` : ''}
          {selected.email ? ` · ${selected.email}` : ''}
          {hint ? ` · ${hint}` : ''}
        </div>
      )}
      {open && results.length > 0 && !selected && (
        <ul style={dropdownStyle} role="listbox">
          {results.map((u) => {
            const selectRow = () => {
              onSelect(u);
              setOpen(false);
            };
            return (
              <li
                key={u.id}
                role="option"
                aria-selected={false}
                tabIndex={0}
                onClick={selectRow}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    selectRow();
                  }
                }}
                style={dropdownItemStyle}
              >
                <div style={{ ...T.bodyBold }}>{fullName(u)}</div>
                <div style={{ ...T.caption, color: PS.textSec }}>
                  {u.phone ?? '—'}{u.email ? ` · ${u.email}` : ''}
                  {u.is_coach ? ' · coach' : ''}{u.is_admin ? ' · admin' : ''}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {open && !searching && results.length === 0 && input.trim() && (
        <div style={{ ...T.caption, color: PS.textSec, marginTop: 4 }}>
          No matches.
        </div>
      )}
    </div>
  );
}

function displayName(u: AdminLookupUser): string {
  const parts = [fullName(u)];
  if (u.phone) parts.push(u.phone);
  return parts.join(' · ');
}

const inputStyle: CSSProperties = {
  padding: '8px 10px',
  border: `1px solid ${PS.border}`,
  borderRadius: 3,
  minHeight: 40,
  width: '100%',
  maxWidth: 400,
  fontFamily: "'Barlow',sans-serif",
  fontSize: 16,
  boxSizing: 'border-box',
};

const dropdownStyle: CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  maxWidth: 400,
  maxHeight: 320,
  overflowY: 'auto',
  margin: 0,
  padding: 0,
  listStyle: 'none',
  background: PS.white,
  border: `1px solid ${PS.border}`,
  borderRadius: 3,
  zIndex: 20,
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
};

const dropdownItemStyle: CSSProperties = {
  padding: '10px 12px',
  cursor: 'pointer',
  borderBottom: `1px solid ${PS.borderLight}`,
};
